import React from 'react';
import { Search, Filter, X, Calendar, Layers, Tag, CheckCircle2, Share2 } from 'lucide-react';
import { FilterState } from '../types/calendar';

interface FilterBarProps {
  filters: FilterState;
  onFilterChange: (filters: FilterState) => void;
  availablePlatforms: string[];
  availablePhases: string[];
  availableFormats: string[];
  availablePillars: string[];
  availableStatuses: string[];
  onShareView?: () => void;
}

export const FilterBar: React.FC<FilterBarProps> = ({
  filters,
  onFilterChange,
  availablePlatforms,
  availablePhases,
  availableFormats,
  availablePillars,
  availableStatuses,
  onShareView,
}) => {
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFilterChange({ ...filters, searchQuery: e.target.value });
  };

  const toggleFilterItem = (category: keyof FilterState, value: string) => {
    const currentList = (filters[category] as string[]) || [];
    const newList = currentList.includes(value)
      ? currentList.filter(item => item !== value)
      : [...currentList, value];
    onFilterChange({ ...filters, [category]: newList });
  };

  const clearAllFilters = () => {
    onFilterChange({
      searchQuery: '',
      selectedPlatforms: [],
      selectedPhases: [],
      selectedFormats: [],
      selectedPillars: [],
      selectedStatuses: [],
      startDate: '',
      endDate: '',
    });
  };

  const activeFilterCount =
    (filters.searchQuery ? 1 : 0) +
    filters.selectedPlatforms.length +
    filters.selectedPhases.length +
    filters.selectedFormats.length +
    filters.selectedPillars.length +
    filters.selectedStatuses.length;

  return (
    <div style={{
      padding: '0.85rem 1.5rem',
      background: 'var(--bg-surface)',
      borderBottom: '1px solid var(--border-color)',
      display: 'flex',
      flexDirection: 'column',
      gap: '0.75rem',
    }}>
      {/* Search and Main Filter Controls */}
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Search Bar */}
        <div style={{
          position: 'relative',
          flex: '1 1 240px',
          minWidth: '220px',
        }}>
          <Search size={16} style={{
            position: 'absolute',
            left: '0.75rem',
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--text-light)',
          }} />
          <input
            type="text"
            placeholder="Search hook, caption, hashtags, CTA, ID..."
            value={filters.searchQuery}
            onChange={handleSearchChange}
            style={{
              width: '100%',
              padding: '0.5rem 0.75rem 0.5rem 2.25rem',
              borderRadius: '8px',
              border: '1px solid var(--border-color)',
              background: 'var(--bg-primary)',
              color: 'var(--text-main)',
              fontSize: '0.85rem',
              outline: 'none',
            }}
          />
          {filters.searchQuery && (
            <button
              onClick={() => onFilterChange({ ...filters, searchQuery: '' })}
              style={{
                position: 'absolute',
                right: '0.6rem',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                color: 'var(--text-light)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <X size={14} />
            </button>
          )}
        </div>

        {/* Share Filtered View Button */}
        {onShareView && (
          <button
            onClick={onShareView}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.4rem',
              padding: '0.45rem 0.85rem',
              borderRadius: '8px',
              border: '1px solid var(--border-color)',
              background: 'var(--bg-primary)',
              color: 'var(--text-main)',
              fontSize: '0.85rem',
              fontWeight: 500,
              transition: 'all 0.2s ease',
            }}
            title="Copy shareable URL link for current view and filters"
          >
            <Share2 size={15} style={{ color: 'var(--accent-primary)' }} />
            Share View URL
          </button>
        )}

        {/* Clear Filters Button */}
        {activeFilterCount > 0 && (
          <button
            onClick={clearAllFilters}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.35rem',
              padding: '0.45rem 0.75rem',
              borderRadius: '8px',
              border: '1px dashed var(--accent-primary)',
              background: 'var(--accent-glow)',
              color: 'var(--accent-primary)',
              fontSize: '0.8rem',
              fontWeight: 600,
            }}
          >
            <X size={14} />
            Reset {activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''}
          </button>
        )}
      </div>

      {/* Filter Tag Pills Row */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
        <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Platforms:
        </span>
        {availablePlatforms.map(p => {
          const isSelected = filters.selectedPlatforms.includes(p);
          return (
            <button
              key={p}
              onClick={() => toggleFilterItem('selectedPlatforms', p)}
              style={{
                padding: '0.2rem 0.55rem',
                borderRadius: '6px',
                border: `1px solid ${isSelected ? 'var(--accent-primary)' : 'var(--border-color)'}`,
                background: isSelected ? 'var(--accent-glow)' : 'var(--bg-primary)',
                color: isSelected ? 'var(--accent-primary)' : 'var(--text-muted)',
                fontSize: '0.75rem',
                fontWeight: isSelected ? 600 : 400,
                transition: 'all 0.15s ease',
              }}
            >
              {p}
            </button>
          );
        })}

        <div style={{ width: '1px', height: '16px', background: 'var(--border-color)', margin: '0 0.25rem' }} />

        <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Phase:
        </span>
        {availablePhases.map(ph => {
          const isSelected = filters.selectedPhases.includes(ph);
          return (
            <button
              key={ph}
              onClick={() => toggleFilterItem('selectedPhases', ph)}
              style={{
                padding: '0.2rem 0.55rem',
                borderRadius: '6px',
                border: `1px solid ${isSelected ? 'var(--accent-primary)' : 'var(--border-color)'}`,
                background: isSelected ? 'var(--accent-glow)' : 'var(--bg-primary)',
                color: isSelected ? 'var(--accent-primary)' : 'var(--text-muted)',
                fontSize: '0.75rem',
                fontWeight: isSelected ? 600 : 400,
                transition: 'all 0.15s ease',
              }}
            >
              {ph}
            </button>
          );
        })}

        <div style={{ width: '1px', height: '16px', background: 'var(--border-color)', margin: '0 0.25rem' }} />

        <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Status:
        </span>
        {availableStatuses.map(s => {
          const isSelected = filters.selectedStatuses.includes(s);
          return (
            <button
              key={s}
              onClick={() => toggleFilterItem('selectedStatuses', s)}
              style={{
                padding: '0.2rem 0.55rem',
                borderRadius: '6px',
                border: `1px solid ${isSelected ? 'var(--accent-primary)' : 'var(--border-color)'}`,
                background: isSelected ? 'var(--accent-glow)' : 'var(--bg-primary)',
                color: isSelected ? 'var(--accent-primary)' : 'var(--text-muted)',
                fontSize: '0.75rem',
                fontWeight: isSelected ? 600 : 400,
                transition: 'all 0.15s ease',
              }}
            >
              {s}
            </button>
          );
        })}
      </div>
    </div>
  );
};
